import { useRef, useState, useEffect } from 'react';
import { Button } from './common/Button';
import { CameraIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface SelfieCaptureProps {
  onCapture: (file: File, location: { latitude: number; longitude: number }, timestamp: Date) => void;
  onClose: () => void;
}

export function SelfieCapture({ onCapture, onClose }: SelfieCaptureProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [location, setLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);

  useEffect(() => {
    // Request camera access
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'user' }, audio: false })
      .then((mediaStream) => {
        setStream(mediaStream);
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      })
      .catch((err) => {
        console.error('Error accessing camera:', err);
        setError('Unable to access camera. Please allow camera permissions.');
      });

    // Request location access
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (err) => {
          console.error('Error getting location:', err);
          setLocationError('Unable to get location. Please allow location permissions.');
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setLocationError('Geolocation is not supported by your browser.');
    }

    // Cleanup
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const capturePhoto = () => {
    console.log("Capture button clicked", { 
      hasVideo: !!videoRef.current, 
      hasCanvas: !!canvasRef.current, 
      hasLocation: !!location,
      videoReady: videoRef.current?.readyState === 4 
    });

    if (!videoRef.current) {
      setError("Video stream not ready. Please wait for camera to load.");
      return;
    }

    if (!canvasRef.current) {
      setError("Canvas not available.");
      return;
    }

    if (!location) {
      setError("Location is required. Please allow location access.");
      return;
    }

    // Check if video is ready
    if (videoRef.current.readyState !== 4) {
      setError("Video stream not ready. Please wait a moment and try again.");
      return;
    }

    setIsCapturing(true);
    setError(null); // Clear any previous errors
    
    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");

      if (!context) {
        setError("Failed to get canvas context");
        setIsCapturing(false);
        return;
      }

      // Set canvas dimensions to a smaller size to reduce file size
      // Max dimensions: 800x600 (maintains aspect ratio)
      const maxWidth = 800;
      const maxHeight = 600;
      
      const videoWidth = video.videoWidth || 640;
      const videoHeight = video.videoHeight || 480;
      
      // Calculate scaled dimensions maintaining aspect ratio
      let canvasWidth = videoWidth;
      let canvasHeight = videoHeight;
      
      if (videoWidth > maxWidth || videoHeight > maxHeight) {
        const aspectRatio = videoWidth / videoHeight;
        if (videoWidth > videoHeight) {
          canvasWidth = maxWidth;
          canvasHeight = maxWidth / aspectRatio;
        } else {
          canvasHeight = maxHeight;
          canvasWidth = maxHeight * aspectRatio;
        }
      }
      
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      // Draw video frame to canvas (scaled)
      context.drawImage(video, 0, 0, canvasWidth, canvasHeight);

      // Convert canvas to blob with reduced quality to further reduce file size
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            setError("Failed to capture image");
            setIsCapturing(false);
            return;
          }

          // Create file from blob
          const file = new File([blob], `selfie_${Date.now()}.jpg`, {
            type: "image/jpeg",
          });

          // Get current timestamp
          const timestamp = new Date();

          console.log("Selfie captured successfully", { 
            fileSize: file.size, 
            fileSizeKB: (file.size / 1024).toFixed(2),
            dimensions: `${canvasWidth}x${canvasHeight}`,
            location, 
            timestamp 
          });

          // Call onCapture with file, location, and timestamp
          onCapture(file, location, timestamp);
          setIsCapturing(false);
        },
        "image/jpeg",
        0.65 // Reduced quality from 0.9 to 0.65 to reduce file size
      );
    } catch (err) {
      console.error("Error capturing photo:", err);
      setError("Failed to capture photo. Please try again.");
      setIsCapturing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Take Selfie for Attendance</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {locationError && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-600">{locationError}</p>
          </div>
        )}

        <div className="relative bg-gray-900 rounded-lg overflow-hidden mb-4" style={{ aspectRatio: '4/3' }}>
          {stream ? (
            <>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-white">
                <CameraIcon className="w-12 h-12 mx-auto mb-2" />
                <p>Loading camera...</p>
              </div>
            </div>
          )}
        </div>

        {location && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-700">
              <strong>Location captured:</strong> {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
            </p>
          </div>
        )}

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              capturePhoto();
            }}
            disabled={!stream || !location || isCapturing}
            isLoading={isCapturing}
            className="flex-1"
          >
            {isCapturing ? 'Capturing...' : 'Capture Selfie'}
          </Button>
        </div>
        {(!stream || !location) && (
          <div className="mt-2 text-xs text-gray-500 text-center">
            {!stream && <p>⏳ Waiting for camera...</p>}
            {!location && <p>📍 Waiting for location...</p>}
          </div>
        )}
      </div>
    </div>
  );
}
